import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div
            className="w-12 h-12 mx-auto mb-4 rounded-full animate-pulse"
            style={{ backgroundColor: '#B8860B' }}
          ></div>
          <p className="text-gray-600">Verifying credentials...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/complaint/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Check admin access for admin routes (allow ADMIN and COLLECTOR; case-insensitive)
  const userRole = (user?.role || '').toUpperCase();
  if (adminOnly && userRole !== 'ADMIN' && userRole !== 'COLLECTOR') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md mx-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access the admin panel. Admin
            privileges are required.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Current role: {user?.role || 'Unknown'}
          </p>
          <div className="space-y-2">
            <button
              onClick={() => window.history.back()}
              className="w-full px-4 py-2 text-white rounded-lg transition-colors duration-200"
              style={{ backgroundColor: '#B8860B' }}
            >
              Go Back
            </button>
            <button
              onClick={() => {
                window.location.href = '/complaint/login';
              }}
              className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
            >
              Login with Admin Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated and authorized, render the protected component
  return children;
};

export default ProtectedRoute;
